import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, formatApiError } from '../auth/AuthContext';
import { useT } from '../i18n/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import OnboardingModal from '../components/OnboardingModal';
import PWAInstallButton from '../components/PWAInstallButton';
import AppDownloadCard from '../components/AppDownloadCard';
import PdfShareCard from '../components/PdfShareCard';
import {
  SECTION_LABELS_DE,
  SECTION_LABELS_EN,
  SECTION_ORDER,
} from '../config/sectionLabels';

/* Tag/Nacht-Vorauswahl für den Presenter-Modus (wirkt beim Start) */
function ThemeSelect() {
  const [light, setLight] = useState(() => localStorage.getItem('stig-theme') === 'light');
  const pick = (v) => {
    setLight(v);
    localStorage.setItem('stig-theme', v ? 'light' : 'dark');
  };
  return (
    <div className="flex items-center gap-2 mr-auto" data-testid="theme-select">
      <span className="text-xs uppercase tracking-[0.16em] text-white/45 font-ui">Modus:</span>
      <button
        type="button"
        data-testid="theme-select-dark"
        onClick={() => pick(false)}
        className={`text-xs uppercase tracking-[0.14em] px-4 py-2 rounded-full border transition-colors ${
          !light ? 'border-[#d4af37] text-[#f2d979] bg-[#d4af3714]' : 'border-white/15 text-white/55 hover:border-white/40'
        }`}
      >
        Nacht (Schwarz)
      </button>
      <button
        type="button"
        data-testid="theme-select-light"
        onClick={() => pick(true)}
        className={`text-xs uppercase tracking-[0.14em] px-4 py-2 rounded-full border transition-colors ${
          light ? 'border-[#d4af37] text-[#f2d979] bg-[#d4af3714]' : 'border-white/15 text-white/55 hover:border-white/40'
        }`}
      >
        Tag (Beige)
      </button>
    </div>
  );
}

function AdminUsers({ api }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'presenter' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const { user: me } = useAuth();

  const load = async () => {
    try {
      const { data } = await axios.get(`${api}/users`);
      setUsers(data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      await axios.post(`${api}/users`, form);
      setForm({ email: '', password: '', name: '', role: 'presenter' });
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('User wirklich löschen?')) return;
    try {
      await axios.delete(`${api}/users/${id}`);
      load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="dash-card" data-testid="admin-users-card">
      <div className="dash-card-head">
        <div className="dash-card-overline">Admin · Presenter-Zugänge</div>
        <h3 className="dash-card-title font-display">Presenter verwalten</h3>
        <p className="dash-card-sub">Lege neue Zugänge an oder entferne bestehende. Gelöschte Nutzer sind sofort ausgesperrt.</p>
      </div>

      <form onSubmit={create} className="admin-create-form" data-testid="admin-create-form">
        <input
          type="email"
          required
          placeholder="E-Mail"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="login-input"
          data-testid="admin-new-email"
        />
        <input
          type="text"
          placeholder="Name (optional)"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="login-input"
          data-testid="admin-new-name"
        />
        <input
          type="password"
          required
          minLength={6}
          placeholder="Passwort (min. 6 Zeichen)"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="login-input"
          data-testid="admin-new-password"
        />
        <select
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
          className="login-input"
          data-testid="admin-new-role"
        >
          <option value="presenter">Presenter</option>
          <option value="admin">Admin</option>
        </select>
        <button type="submit" disabled={busy} className="dash-btn dash-btn-gold" data-testid="admin-create-btn">
          {busy ? 'Anlegen…' : 'Anlegen'}
        </button>
        {err && <div className="login-error" data-testid="admin-create-error">{err}</div>}
      </form>

      <ul className="admin-user-list" data-testid="admin-user-list">
        {users.map((u) => (
          <li key={u.id} className="admin-user-row">
            <div>
              <div className="admin-user-email">{u.email}</div>
              <div className="admin-user-meta">
                {u.name ? `${u.name} · ` : ''}
                <span className={u.role === 'admin' ? 'admin-badge admin' : 'admin-badge presenter'}>
                  {u.role}
                </span>
              </div>
            </div>
            {u.id !== me.id ? (
              <button
                type="button"
                onClick={() => remove(u.id)}
                className="admin-delete-btn"
                data-testid={`admin-delete-${u.email}`}
              >
                Löschen
              </button>
            ) : (
              <span className="admin-badge admin">Du</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PdfRegenerateBar({ API }) {
  const [status, setStatus] = useState('idle'); // idle | running | done | error
  const [error, setError] = useState('');
  const [fileInfo, setFileInfo] = useState(null);

  // Fetch initial state once, then poll while running.
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const pull = async () => {
      try {
        const { data } = await axios.get(`${API}/pdf/status`);
        if (cancelled) return;
        setStatus(data.status || 'idle');
        setError(data.error || '');
        setFileInfo(data.file || null);
        if (data.status === 'running') {
          timer = setTimeout(pull, 2500);
        }
      } catch (_e) {
        /* ignore polling errors */
      }
    };
    pull();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [API]);

  const start = async () => {
    setStatus('running');
    setError('');
    try {
      await axios.post(`${API}/pdf/generate`);
      const tick = async () => {
        try {
          const { data } = await axios.get(`${API}/pdf/status`);
          setStatus(data.status || 'idle');
          setError(data.error || '');
          setFileInfo(data.file || null);
          if (data.status === 'running') setTimeout(tick, 2500);
        } catch (_e) { /* ignore */ }
      };
      setTimeout(tick, 2000);
    } catch (e) {
      setStatus('error');
      setError(e.message || 'Unbekannter Fehler');
    }
  };

  const humanSize = fileInfo ? `${(fileInfo.size_bytes / 1024 / 1024).toFixed(1)} MB` : '';
  const humanTime = fileInfo
    ? new Date(fileInfo.modified_at).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : '';

  return (
    <div className="dash-pdf-regen" data-testid="dash-pdf-regen">
      <div className="dash-pdf-regen-info">
        <span className="dash-pdf-regen-kicker">ADMIN · PDF-BUILD</span>
        <span className="dash-pdf-regen-title">
          {status === 'running'
            ? 'Erstelle neue PDF … das dauert ~90 Sekunden'
            : status === 'done'
            ? 'PDF ist auf dem neuesten Stand'
            : status === 'error'
            ? 'Letzter Build ist fehlgeschlagen'
            : 'PDF nach Content-Änderungen neu bauen'}
        </span>
        {fileInfo && (
          <span className="dash-pdf-regen-meta">
            Letzter Stand · {humanTime} · {humanSize}
          </span>
        )}
        {status === 'error' && error && (
          <span className="dash-pdf-regen-err">{error.slice(-200)}</span>
        )}
      </div>
      <button
        type="button"
        onClick={start}
        disabled={status === 'running'}
        className="dash-pdf-regen-btn"
        data-testid="dash-pdf-regen-btn"
      >
        {status === 'running' ? 'Baut …' : 'PDF neu generieren'}
      </button>
    </div>
  );
}

export default function DashboardPage() {
  const { user, logout, saveConfig, API } = useAuth();
  const { lang } = useT();
  const nav = useNavigate();

  const [agenda, setAgenda] = useState([]);
  const [newAgenda, setNewAgenda] = useState('');
  const [visibility, setVisibility] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');
  const [downloadsEnabled, setDownloadsEnabledState] = useState(
    () => localStorage.getItem('stig-downloads-enabled') !== 'off',
  );
  const setDownloadsEnabled = (v) => {
    setDownloadsEnabledState(v);
    localStorage.setItem('stig-downloads-enabled', v ? 'on' : 'off');
  };

  const labels = lang === 'de' ? SECTION_LABELS_DE : SECTION_LABELS_EN;

  useEffect(() => {
    if (user && user.id) {
      setAgenda(user.agenda || []);
      const v = {};
      SECTION_ORDER.forEach((k) => {
        v[k] = user.section_visibility && user.section_visibility[k] !== undefined
          ? user.section_visibility[k]
          : true;
      });
      setVisibility(v);
    }
  }, [user]);

  const visibleCount = useMemo(
    () => Object.values(visibility).filter(Boolean).length,
    [visibility],
  );

  if (!user || !user.id) return null;

  const addAgenda = () => {
    const v = newAgenda.trim();
    if (!v) return;
    setAgenda([...agenda, v]);
    setNewAgenda('');
  };

  const updateAgendaAt = (i, val) => {
    const copy = [...agenda];
    copy[i] = val;
    setAgenda(copy);
  };

  const removeAgenda = (i) => setAgenda(agenda.filter((_, idx) => idx !== i));

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= agenda.length) return;
    const copy = [...agenda];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setAgenda(copy);
  };

  const toggleAll = (val) => {
    const v = {};
    SECTION_ORDER.forEach((k) => { v[k] = val; });
    setVisibility(v);
  };

  const save = async () => {
    setSaving(true); setErr(''); setSaved(false);
    try {
      await saveConfig(agenda, visibility);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const startPresentation = async () => {
    setSaving(true);
    // Echtes Browser-Vollbild (wie PPTX) — muss synchron in der User-Geste
    // angefordert werden, sonst blockiert es der Browser.
    try {
      const el = document.documentElement;
      const req = el.requestFullscreen || el.webkitRequestFullscreen;
      if (req && !document.fullscreenElement) req.call(el).catch?.(() => {});
    } catch (e) { /* Fullscreen nicht verfügbar — Präsentation startet trotzdem */ }
    try {
      await saveConfig(agenda, visibility);
      nav('/present');
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="dash-shell" data-testid="dashboard-page">
      <OnboardingModal />
      <div className="login-bg-grain" aria-hidden />
      <div className="login-bg-radial" aria-hidden />

      <header className="dash-header">
        <div className="login-brandmark">
          <span className="login-brandmark-icon">◆</span>
          <span className="login-brandmark-text">STIG&nbsp;GROUP</span>
          <span className="dash-header-sub">Presenter Cockpit</span>
        </div>
        <div className="dash-header-right">
          <span className="dash-user-chip" data-testid="dash-user-chip">
            <span className={user.role === 'admin' ? 'admin-badge admin' : 'admin-badge presenter'}>
              {user.role}
            </span>
            <span>{user.email}</span>
          </span>
          <LanguageSwitcher variant="dark" />
          <button type="button" onClick={logout} className="dash-mini-btn" data-testid="dash-logout">
            Logout
          </button>
        </div>
      </header>

      <main className="dash-main">
        <div className="dash-hero">
          <div className="overline">Vor dem Auftritt</div>
          <h1 className="dash-hero-title font-display">Konfiguriere deine Präsentation</h1>
          <p className="dash-hero-sub">
            Stelle deine Agenda zusammen und wähle exakt die Folien, die dein Publikum sehen soll.
            Änderungen werden für <em>dein</em> Konto gespeichert und beim Start der Präsentation angewandt.
          </p>
          {/* PWAInstallButton entfernt (User-Wunsch 08/2026) — Desktop-App-Download ersetzt Offline-PWA */}
          <AppDownloadCard />
          {user.role === 'admin' && <PdfRegenerateBar API={API} />}
          {user.role === 'admin' && (
            <div className="dash-downloads-toggle" data-testid="dash-downloads-toggle-row">
              <label className="dash-downloads-toggle-label">
                <input
                  type="checkbox"
                  checked={downloadsEnabled}
                  onChange={(e) => setDownloadsEnabled(e.target.checked)}
                  data-testid="dash-downloads-toggle"
                />
                <span>Downloads aktiv (abschaltbar)</span>
              </label>
            </div>
          )}
          <div
            className={`dash-pdf-links ${downloadsEnabled ? '' : 'dash-pdf-links--disabled'}`}
            aria-disabled={!downloadsEnabled}
          >
            {[
              {
                lang: 'de',
                heading: 'Deutsch',
                kicker: 'PRÄSENTATION · PDF · DE',
                variants: [
                  { key: 'full',    label: 'Komplette Präsentation', sub: 'Alle 33 Folien' },
                  { key: 'product', label: 'Produktpräsentation',    sub: 'Hero → „Der einfache Weg" · 25 Folien' },
                  { key: 'plan',    label: 'Vergütungsplan',         sub: '„Der einfachste und fairste Weg" → Instagram · 9 Folien' },
                ],
              },
              {
                lang: 'en',
                heading: 'English',
                kicker: 'PRESENTATION · PDF · EN',
                variants: [
                  { key: 'full',    label: 'Complete presentation', sub: 'All 33 slides' },
                  { key: 'product', label: 'Product presentation',  sub: 'Hero → "The simple way" · 25 slides' },
                  { key: 'plan',    label: 'Compensation plan',     sub: '"The simplest and fairest way" → Instagram · 9 slides' },
                ],
              },
              {
                lang: 'tr',
                heading: 'Türkçe',
                kicker: 'SUNUM · PDF · TR',
                variants: [
                  { key: 'full',    label: 'Tam sunum',      sub: 'Tüm 33 slayt' },
                  { key: 'product', label: 'Ürün sunumu',    sub: 'Hero → „Basit yol" · 25 slayt' },
                  { key: 'plan',    label: 'Ödeme planı',    sub: '„En basit ve adil yol" → Instagram · 9 slayt' },
                ],
              },
            ].map((section) => (
              <div key={section.lang} className="dash-pdf-lang-block" data-testid={`dash-pdf-lang-${section.lang}`}>
                <div className="dash-pdf-lang-heading">{section.heading}</div>
                {section.variants.map((v) => {
                  const fname = v.key === 'full'
                    ? `stig-rock-presentation-${section.lang}.pdf`
                    : v.key === 'product'
                      ? `stig-rock-produkt-${section.lang}.pdf`
                      : `stig-rock-plan-${section.lang}.pdf`;
                  const url = v.key === 'full'
                    ? `${API}/pdf/download/${section.lang}?v=${(user && user.id) || ''}`
                    : `${API}/pdf/download/${section.lang}/${v.key}?v=${(user && user.id) || ''}`;
                  return (
                    <a
                      key={v.key}
                      href={downloadsEnabled ? url : undefined}
                      download={fname}
                      className="dash-pdf-link"
                      data-testid={`dash-download-${section.lang}-${v.key}`}
                      onClick={(e) => { if (!downloadsEnabled) e.preventDefault(); }}
                    >
                      <span className="dash-pdf-link-kicker">{section.kicker} · {v.key.toUpperCase()}</span>
                      <span className="dash-pdf-link-title">{v.label}</span>
                      <span className="dash-pdf-link-sub">{v.sub}</span>
                    </a>
                  );
                })}
              </div>
            ))}
            {/* Offline-Bundle (Mac + Windows, autark) */}
            <div className="dash-pdf-lang-block" data-testid="dash-pdf-lang-offline">
              <div className="dash-pdf-lang-heading">Offline (English)</div>
              <a
                href="/stig-rock-offline-en.zip"
                download="stig-rock-offline-en.zip"
                className="dash-pdf-link"
                data-testid="dash-download-offline-en"
              >
                <span className="dash-pdf-link-kicker">OFFLINE BUNDLE · ZIP · EN</span>
                <span className="dash-pdf-link-title">Vollständige Offline-Präsentation</span>
                <span className="dash-pdf-link-sub">
                  Mac + Windows · Doppelklick auf PRAESENTATION-STARTEN.html · kein Terminal, keine Installation
                </span>
              </a>
            </div>
            {/* PPTX-Karte entfernt (User-Wunsch 08/2026) */}
          </div>
        </div>

        <div className="dash-grid">
          {/* Agenda editor */}
          <div className="dash-card" data-testid="agenda-card">
            <div className="dash-card-head">
              <div className="dash-card-overline">Individuelle Agenda</div>
              <h3 className="dash-card-title font-display">Agenda-Punkte</h3>
              <p className="dash-card-sub">
                Diese Punkte erscheinen als goldene Agenda-Folie direkt vor dem Disclaimer.
                Bleibt die Liste leer, wird die Agenda-Folie automatisch übersprungen.
              </p>
            </div>

            <ul className="agenda-list" data-testid="agenda-list">
              {agenda.map((item, i) => (
                <li key={i} className="agenda-row" data-testid={`agenda-row-${i}`}>
                  <span className="agenda-num">{String(i + 1).padStart(2, '0')}</span>
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateAgendaAt(i, e.target.value)}
                    className="agenda-input"
                    data-testid={`agenda-input-${i}`}
                  />
                  <div className="agenda-actions">
                    <button type="button" onClick={() => move(i, -1)} className="agenda-mini" title="hoch">▲</button>
                    <button type="button" onClick={() => move(i, +1)} className="agenda-mini" title="runter">▼</button>
                    <button type="button" onClick={() => removeAgenda(i)} className="agenda-mini agenda-remove" data-testid={`agenda-remove-${i}`}>×</button>
                  </div>
                </li>
              ))}
              {agenda.length === 0 && (
                <li className="agenda-empty">Noch keine Agenda-Punkte hinzugefügt.</li>
              )}
            </ul>

            <div className="agenda-add">
              <input
                type="text"
                value={newAgenda}
                onChange={(e) => setNewAgenda(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAgenda(); } }}
                placeholder="Neuer Agenda-Punkt … z. B. Marktüberblick"
                className="agenda-input"
                data-testid="agenda-new-input"
              />
              <button
                type="button"
                onClick={addAgenda}
                className="dash-btn dash-btn-outline"
                data-testid="agenda-add-btn"
              >
                Hinzufügen
              </button>
            </div>
          </div>

          {/* Section visibility */}
          <div className="dash-card" data-testid="sections-card">
            <div className="dash-card-head">
              <div className="dash-card-overline">Folien-Auswahl</div>
              <h3 className="dash-card-title font-display">Sichtbare Folien</h3>
              <p className="dash-card-sub">
                Aktiviere die Sektionen, die du in deiner Präsentation zeigen möchtest.
                Hero, Disclaimer und Footer werden immer angezeigt.
                <span className="dash-count" data-testid="sections-count">
                  {' '}{visibleCount} / {SECTION_ORDER.length} sichtbar
                </span>.
              </p>
              <div className="dash-toggle-actions">
                <button type="button" onClick={() => toggleAll(true)} className="dash-mini-btn" data-testid="sections-all-on">
                  Alle an
                </button>
                <button type="button" onClick={() => toggleAll(false)} className="dash-mini-btn" data-testid="sections-all-off">
                  Alle aus
                </button>
              </div>
            </div>

            <div className="section-toggle-list" data-testid="section-toggle-list">
              {SECTION_ORDER.map((k) => (
                <label key={k} className="section-toggle" data-testid={`section-toggle-${k}`}>
                  <input
                    type="checkbox"
                    checked={!!visibility[k]}
                    onChange={(e) => setVisibility({ ...visibility, [k]: e.target.checked })}
                    data-testid={`section-toggle-input-${k}`}
                  />
                  <span className="section-toggle-track"><span className="section-toggle-thumb" /></span>
                  <span className="section-toggle-label">{labels[k]}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {user.role === 'admin' && <AdminUsers api={API} />}

        <PdfShareCard />

        <div className="dash-actionbar" data-testid="dash-actionbar">
          <ThemeSelect />
          <button type="button" onClick={save} disabled={saving} className="dash-btn dash-btn-outline" data-testid="dash-save-btn">
            {saving ? 'Speichere…' : 'Einstellungen speichern'}
          </button>
          <button type="button" onClick={startPresentation} disabled={saving} className="dash-btn dash-btn-gold dash-btn-large" data-testid="dash-start-btn">
            Präsentation starten →
          </button>
          {saved && <span className="dash-saved" data-testid="dash-saved">✓ gespeichert</span>}
          {err && <span className="login-error" data-testid="dash-err">{err}</span>}
        </div>
      </main>

      <footer className="dash-footer">© {new Date().getFullYear()} Stig Group · Vertraulich</footer>
    </div>
  );
}
